import React from 'react';
import { cn } from '../../lib/utils';

export type LoadingSpinnerSize = 'sm' | 'md' | 'lg' | 'xl';
export type LoadingSpinnerVariant = 'primary' | 'secondary' | 'white' | 'current';

interface LoadingSpinnerProps {
  size?: LoadingSpinnerSize;
  variant?: LoadingSpinnerVariant;
  className?: string;
}

const sizeClasses: Record<LoadingSpinnerSize, string> = {
  sm: 'h-3 w-3',
  md: 'h-4 w-4',
  lg: 'h-8 w-8',
  xl: 'h-12 w-12',
};

const variantClasses: Record<LoadingSpinnerVariant, string> = {
  primary: 'border-primary',
  secondary: 'border-blue-600',
  white: 'border-white',
  current: 'border-current',
};

/**
 * Unified loading spinner component
 * 
 * Usage:
 * - Inline: <LoadingSpinner size="sm" />
 * - With text: <LoadingSpinner size="md" /> <span>Loading...</span>
 * - In buttons: <LoadingSpinner size="sm" variant="white" />
 */
export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'lg',
  variant = 'primary',
  className,
}) => {
  return (
    <div
      className={cn(
        'animate-spin rounded-full border-b-2',
        sizeClasses[size],
        variantClasses[variant],
        className
      )}
      role="status"
      aria-label="Loading"
    >
      <span className="sr-only">Loading...</span>
    </div>
  );
};
